import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  UserCircleIcon,
  ChatTeardropDotsIcon,
  EnvelopeSimpleIcon,
  SpeakerSlashIcon,
  SignOutIcon,
  ShieldStarIcon,
} from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Click-to-reveal popover for any user name displayed inside a live show
 * (chat messages + viewer list + slide-in joins + chat avatars).
 *
 * Standard actions (everyone):
 *  - **View profile** → /seller/{user_id}
 *  - **Reply in chat** → inserts `@username ` into the live chat input
 *  - **Send DM** (optional) → /messages?thread_id={id}
 *
 * Mod actions (when `isStreamMod` is true and target is not self/seller):
 *  - **Mute** for 15 minutes
 *  - **Kick** from this stream
 *  Both call `/api/streams/{streamId}/mod/...` so seller / global mod /
 *  appointed stream-mods can all run them.
 *
 * Renders nothing if the target user has no `user_id` (anonymous viewer).
 */
export default function UserNamePopover({
  userId,
  username,
  displayName,
  onReply,
  enableDm = true,
  isStreamMod = false,
  streamId,
  sellerId,
  currentUserId,
  className = "",
  children,
  testid,
  // When true, clicking the username DIRECTLY inserts `@username ` into the
  // chat input (via onReply) instead of opening the menu. platform-style
  // one-tap reply UX. Used in the live chat feed. The full menu is still
  // available everywhere else (Watching tab, etc.) for profile + DM + mod
  // actions.
  tapToReply = false,
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [open]);

  if (!userId) {
    // Anonymous / system message — render the children plain.
    return <span className={className}>{children}</span>;
  }

  const handle = username || displayName || "user";
  // Mod actions are gated: stream-mods can't mute/kick themselves or the
  // seller; the API enforces this too but we hide the buttons preemptively.
  const canMod =
    isStreamMod &&
    !!streamId &&
    userId !== currentUserId &&
    userId !== sellerId;

  const goProfile = () => {
    setOpen(false);
    navigate(`/seller/${userId}`);
  };

  const replyInChat = () => {
    setOpen(false);
    onReply?.(handle);
  };

  const sendDm = async () => {
    setOpen(false);
    try {
      const { data } = await api.post("/dm/threads", { user_id: userId });
      navigate(`/messages?thread_id=${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const muteUser = async () => {
    setBusy(true);
    try {
      await api.post(`/streams/${streamId}/mod/mute/${userId}`, {
        duration_minutes: 15,
      });
      toast.success(`Muted @${handle} for 15 min`);
      setOpen(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const kickUser = async () => {
    setBusy(true);
    try {
      await api.post(`/streams/${streamId}/kick/${userId}`);
      toast.success(`Kicked @${handle}`);
      setOpen(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <span ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (tapToReply && onReply) {
            onReply(handle);
            return;
          }
          setOpen((v) => !v);
        }}
        className={`hover:underline cursor-pointer ${className}`}
        data-testid={testid || `username-popover-${userId}`}
      >
        {children}
      </button>
      {open && (
        <span
          className="absolute top-full left-0 mt-1 z-40 w-48 bg-white border border-zinc-200 rounded-xl shadow-2xl overflow-hidden"
          data-testid={`username-popover-open-${userId}`}
        >
          <button
            type="button"
            onClick={goProfile}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-zinc-50 text-zinc-700"
            data-testid={`username-popover-profile-${userId}`}
          >
            <UserCircleIcon size={14} />
            View profile
          </button>
          {onReply && (
            <button
              type="button"
              onClick={replyInChat}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-zinc-50 text-[#1D78AB]"
              data-testid={`username-popover-reply-${userId}`}
            >
              <ChatTeardropDotsIcon size={14} />
              Reply in chat
            </button>
          )}
          {enableDm && (
            <button
              type="button"
              onClick={sendDm}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-zinc-50 text-zinc-700 border-t border-zinc-100"
              data-testid={`username-popover-dm-${userId}`}
            >
              <EnvelopeSimpleIcon size={14} />
              Send a DM
            </button>
          )}
          {canMod && (
            <>
              <div
                className="px-3 py-1 bg-zinc-50 text-[9px] uppercase tracking-widest text-zinc-500 font-bold flex items-center gap-1 border-t border-zinc-100"
              >
                <ShieldStarIcon size={10} weight="fill" /> Mod actions
              </div>
              <button
                type="button"
                onClick={muteUser}
                disabled={busy}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-zinc-50 text-amber-700 disabled:opacity-50"
                data-testid={`username-popover-mute-${userId}`}
              >
                <SpeakerSlashIcon size={14} />
                Mute (15 min)
              </button>
              <button
                type="button"
                onClick={kickUser}
                disabled={busy}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-zinc-50 text-red-700 disabled:opacity-50"
                data-testid={`username-popover-kick-${userId}`}
              >
                <SignOutIcon size={14} />
                Kick from stream
              </button>
            </>
          )}
        </span>
      )}
    </span>
  );
}
